// Payroll Excel parsing — pure functions, no React/Supabase, so the logic is
// testable outside the browser. Used by the Payroll screen (Stage 2) and later
// by slip generation.
//
// Sheet selection is NOT hardcoded: readWorkbook() reports every sheet and
// which expected columns it carries; the screen lets staff pick (preselecting
// the sheet when exactly one qualifies), then parseSheet() parses that one.
import * as XLSX from 'xlsx';

// Headers matched by normalized name, never by column position. One source
// header is "Basic " with a trailing space — normalization trims it.
export const EXPECTED_COLUMNS = [
  { key: 'sl_no',            header: 'Sl No' },
  { key: 'name',             header: 'Employee Name' },
  { key: 'employee_code',    header: 'Employee code' },
  { key: 'department',       header: 'Department' },
  { key: 'phone',            header: 'Phone number' },
  { key: 'uan',              header: 'UAN' },
  { key: 'payment_type',     header: 'Payment Type' },
  { key: 'account_number',   header: 'Account Number' },
  { key: 'gross',            header: 'Gross Monthly' },
  { key: 'basic',            header: 'Basic' },
  { key: 'da',               header: 'DA' },
  { key: 'residual_pay',     header: 'Residual Pay' },
  { key: 'pf',               header: 'PF' },
  { key: 'esic',             header: 'ESIC' },
  { key: 'professional_tax', header: 'Professional Tax' },
];

export const normHeader = (h) => String(h ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

// Phone / UAN / account number must survive as text. If Excel stored them as
// numbers, String(v) keeps every digit the file still has (JS only switches to
// exponent notation at 1e21); it never produces "9.87654e9" for these ranges.
export const cellText = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return String(v);
  return String(v).trim();
};

export const parseMoney = (v) => {
  if (v === null || v === undefined || String(v).trim() === '') return null;
  const n = Number(String(v).replace(/,/g, '').trim());
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : NaN;
};

// Strip spaces/dashes; fold +91 / 91 / 0 prefixes down to the 10-digit mobile.
export const normalizePhone = (raw) => {
  let p = cellText(raw).replace(/[\s-]/g, '');
  if (p.startsWith('+91')) p = p.slice(3);
  else if (p.length === 12 && p.startsWith('91')) p = p.slice(2);
  else if (p.length === 11 && p.startsWith('0')) p = p.slice(1);
  return p;
};

export const isValidMobile = (p) => /^[6-9]\d{9}$/.test(p);

// ─── Shared validation rules ──────────────────────────────────────────────────
// ONE implementation for the upload parser, the review screen, and the send
// step. Stage 5 must gate dispatch on isSendablePhone — the same rule that
// makes the review screen flag a row — so a flagged number can never be sent.

export const isSendablePhone = (p) => !!p && isValidMobile(p);

// null → fine; 'missing' → absent (slip shows "NA"); 'invalid' → present but not 12 digits.
export const uanIssue = (uan) => {
  if (!uan) return 'missing';
  return /^\d{12}$/.test(uan) ? null : 'invalid';
};

// Returns the Basic + DA + Residual Pay sum when it disagrees with gross
// (beyond 1 paisa), else null. Never alters either figure.
export const grossMismatch = (gross, basic, da, residual) => {
  if (gross === null || gross === undefined || Number.isNaN(gross)) return null;
  const sum = Math.round((((basic ?? 0) + (da ?? 0) + (residual ?? 0)) * 100)) / 100;
  return Math.abs(gross - sum) > 0.01 ? sum : null;
};

const MONEY_FIELDS = [
  ['gross', 'Gross Monthly'], ['basic', 'Basic'], ['da', 'DA'],
  ['residual_pay', 'Residual Pay'], ['pf', 'PF'], ['esic', 'ESIC'],
  ['professional_tax', 'Professional Tax'],
];

// Warnings for a STORED payslip row (joined with its employee), recomputed
// from the database by the review screen. Same rules as the upload parser,
// plus the one warning only the database can know: figures changed after the
// slip was sent.
export function payslipWarnings(slip) {
  const emp = slip.employees || {};
  const messages = [];

  if (!emp.phone) messages.push('No phone number — slip cannot be sent.');
  else if (!isSendablePhone(emp.phone)) messages.push(`Phone "${emp.phone}" is not a valid mobile — slip cannot be sent.`);

  const uan = uanIssue(emp.uan);
  if (uan === 'missing') messages.push('Missing UAN — slip will show "NA".');
  else if (uan === 'invalid') messages.push(`UAN "${emp.uan}" is not 12 digits.`);

  const missingFigures = MONEY_FIELDS.filter(([k]) => slip[k] === null || slip[k] === undefined).map(([, label]) => label);
  if (missingFigures.length) messages.push(`Missing figure(s): ${missingFigures.join(', ')}.`);

  const sum = grossMismatch(slip.gross, slip.basic, slip.da, slip.residual_pay);
  if (sum !== null) messages.push(`Arithmetic mismatch: Gross Monthly is ${slip.gross} but Basic + DA + Residual Pay = ${sum}.`);

  // 5s margin: the send step stamps sent_at while the DB trigger stamps
  // updated_at in the same transaction — near-identical times must not warn.
  if (slip.sent_at && slip.updated_at && (new Date(slip.updated_at) - new Date(slip.sent_at)) > 5000) {
    messages.push('Figures changed after this slip was sent — the employee has the old version. Re-send after checking.');
  }

  return messages;
}

// Locate the header row in raw rows: the row matching the most expected headers.
function findHeaders(raw) {
  let headerIdx = -1;
  let colMap = {};
  for (let i = 0; i < Math.min(raw.length, 10); i++) {
    const tmp = {};
    let found = 0;
    for (let j = 0; j < raw[i].length; j++) {
      const norm = normHeader(raw[i][j]);
      const col = EXPECTED_COLUMNS.find((c) => normHeader(c.header) === norm);
      if (col && tmp[col.key] === undefined) { tmp[col.key] = j; found++; }
    }
    if (found > Object.keys(colMap).length) { colMap = tmp; headerIdx = i; }
  }
  return { headerIdx, colMap };
}

const sheetRaw = (ws) => XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

// Read the workbook and describe every sheet: which expected columns it has,
// which are missing, and how many data rows sit under its header row. The
// screen uses this to build the sheet picker.
export function readWorkbook(buffer) {
  const wb = XLSX.read(new Uint8Array(buffer), { type: 'array' });
  const sheets = wb.SheetNames.map((name) => {
    const raw = sheetRaw(wb.Sheets[name]);
    const { headerIdx, colMap } = findHeaders(raw);
    const missing = EXPECTED_COLUMNS.filter((c) => colMap[c.key] === undefined).map((c) => c.header);
    let dataRows = 0;
    if (headerIdx !== -1 && missing.length === 0) {
      for (let i = headerIdx + 1; i < raw.length; i++) {
        if (raw[i].some((c) => cellText(c) !== '')) dataRows++;
      }
    }
    return { name, missing, complete: missing.length === 0, dataRows };
  });
  return { wb, sheets };
}

// Parse one named sheet. Returns { rows, cashExcluded, warnings } or
// { error, missing } when the sheet lacks expected columns — the screen keeps
// the picker up so a different sheet can be chosen without re-uploading.
export function parseSheet(wb, sheetName) {
  const ws = wb.Sheets[sheetName];
  if (!ws) return { error: `Sheet "${sheetName}" not found in this workbook.` };

  const raw = sheetRaw(ws);
  if (!raw.length) return { error: `Sheet "${sheetName}" is empty.` };

  const { headerIdx, colMap } = findHeaders(raw);
  const missingCols = EXPECTED_COLUMNS.filter((c) => colMap[c.key] === undefined);
  if (headerIdx === -1 || missingCols.length) {
    return {
      missing: missingCols.map((c) => c.header),
      error: `Refusing to import from "${sheetName}" — expected column(s) not found: ${missingCols.map((c) => `"${c.header}"`).join(', ') || 'no header row recognised'}. Nothing was guessed or skipped. Pick a different sheet, or fix the file.`,
    };
  }

  const rows = [];
  const cashExcluded = [];
  const warnings = []; // { code, name, messages: [] }
  const seenCodes = new Set();

  for (let i = headerIdx + 1; i < raw.length; i++) {
    const r = raw[i];
    const get = (key) => r[colMap[key]];

    const name = cellText(get('name'));
    const employee_code = cellText(get('employee_code'));
    const grossRaw = get('gross');
    if (!name && !employee_code && cellText(grossRaw) === '') continue; // blank row

    const rowLabel = name || employee_code || `row ${i + 1}`;
    const messages = [];

    if (!employee_code) {
      warnings.push({ code: '', name: rowLabel, messages: ['No employee code — row skipped (cannot upsert without one).'] });
      continue;
    }
    if (seenCodes.has(employee_code)) {
      warnings.push({ code: employee_code, name: rowLabel, messages: ['Duplicate employee code in file — this later row was skipped.'] });
      continue;
    }
    seenCodes.add(employee_code);

    const payment_type = cellText(get('payment_type'));
    if (payment_type.toLowerCase() === 'cash') {
      cashExcluded.push(`${name || employee_code} (${employee_code})`);
      continue; // excluded entirely: no employee upsert, no payslip, no send
    }

    const phone = normalizePhone(get('phone'));
    if (!phone) messages.push('No phone number.');
    else if (!isValidMobile(phone)) messages.push(`Phone "${phone}" does not look like a valid mobile number.`);

    const uanRaw = cellText(get('uan'));
    const uan = uanRaw.replace(/\s/g, '');
    const uanProblem = uanIssue(uan);
    if (uanProblem === 'missing') messages.push('Missing UAN (slip will show "NA").');
    else if (uanProblem === 'invalid') messages.push(`UAN "${uan}" is not 12 digits.`);

    const acctRaw = get('account_number');
    const account_number = cellText(acctRaw);
    if (typeof acctRaw === 'number' && acctRaw >= 1e15) {
      messages.push('Account number was stored as a number in Excel — digits beyond 15 may already be lost in the file. Verify against the bank record.');
    }

    const money = {};
    for (const k of ['gross', 'basic', 'da', 'residual_pay', 'pf', 'esic', 'professional_tax']) {
      money[k] = parseMoney(get(k));
      if (Number.isNaN(money[k])) messages.push(`"${EXPECTED_COLUMNS.find((c) => c.key === k).header}" is not a number ("${cellText(get(k))}").`);
    }

    const mismatchSum = grossMismatch(money.gross, money.basic, money.da, money.residual_pay);
    if (mismatchSum !== null) {
      messages.push(`Arithmetic mismatch: Gross Monthly is ${money.gross} but Basic + DA + Residual Pay = ${mismatchSum}. Neither figure was corrected — fix the sheet.`);
    }

    if (messages.length) warnings.push({ code: employee_code, name: rowLabel, messages });

    rows.push({
      employee_code,
      name,
      department: cellText(get('department')),
      phone,
      uan: uan || null,
      payment_type,
      account_number,
      gross: Number.isNaN(money.gross) ? null : money.gross,
      basic: Number.isNaN(money.basic) ? null : money.basic,
      da: Number.isNaN(money.da) ? null : money.da,
      residual_pay: Number.isNaN(money.residual_pay) ? null : money.residual_pay,
      pf: Number.isNaN(money.pf) ? null : money.pf,
      esic: Number.isNaN(money.esic) ? null : money.esic,
      professional_tax: Number.isNaN(money.professional_tax) ? null : money.professional_tax,
    });
  }

  return { rows, cashExcluded, warnings };
}
